import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import SidebarStudent from "./Utils/SidebarStudent";
import LogoutButton from "./LogoutButton";

import CreateJeemain, {
    JeeMainApplicationFormRef,
} from "./Resource/CreateJeemain";

import CreateJeeadvanced, {
    JeeAdvancedApplicationFormRef,
} from "./Resource/CreateJeeadvanced";

import CreateOlympiadExport, {
    OlympiadFormRef,
} from "./Resource/CreateOlympiad";

import { toast } from "../utils/toast";
import { useStudentProgress } from "../context/StudentProgressContext";

import "./Dashboard.css";
import { useExamdetailsViewModel } from "../viewModels/useExamDetailsViewMore";
import { useAdmissionDriveKind } from "../hooks/useAdmissionDriveKind";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import apiConfig from "../config/apiConfig";
import { apiFetch } from "../apis/apiFetch";
import { LoginContext } from "../context/LoginContext";
import { useContext, useEffect } from "react";

export default function ExamDetails() {
    const navigate = useNavigate();
    const { refreshProgress, isAppSubmitted } = useStudentProgress();

    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // ✅ Refs for all forms
    const jeMainRef = useRef<JeeMainApplicationFormRef>(null);
    const jeAdvRef = useRef<JeeAdvancedApplicationFormRef>(null);
    const olympiadRef = useRef<OlympiadFormRef>(null);

    const { kind: admissionKind, isDriveEditOpen } = useAdmissionDriveKind();
    const queryClient = useQueryClient();

    // assuming userId + appId available (same as other pages)
    const userId = "student"; // replace if from context
    const appId = "admission-portal";

    const {
        dataToSave,
        setDataToSave,
        save,
    } = useExamdetailsViewModel(userId, appId);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showToast, setShowToast] = useState(false);

    const { user } = useContext(LoginContext);

    const { data: dataRes, isFetching } = useQuery({
        queryKey: ["resourceData", "examDetails", user?.userId, localStorage.getItem("activeDriveId")],
        queryFn: async () => {
            if (!user?.userId) return [];

            const response = await apiFetch(
                `${apiConfig.getResourceUrl("applications")}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId}`,
                { method: "GET" }
            );
            // console.log("dataRes1CalledAPI", hasPrePopulated)
            if (!response.ok) {
                throw new Error("Error: " + response.status);
            }

            const data = await response.json();
            return data.resource || [];
        },
        enabled: !!user?.userId,
    });

    const hasPrePopulated = useRef(false);
    // console.log("dataRes1", dataRes, hasPrePopulated);

    useEffect(() => {
        const activeDriveId = localStorage.getItem("activeDriveId");
        if (!hasPrePopulated.current && !isFetching && dataRes && dataRes.length > 0 && activeDriveId) {
            const userRecord = dataRes.find((r: any) => r.user_id === user?.userId && r.drive_id === activeDriveId);

            if (userRecord) {
                console.log("dataResdataRes", userRecord)
                hasPrePopulated.current = true;

                setDataToSave((prev: any) => ({
                    ...userRecord,   // ✅ DB data
                    ...prev,         // ✅ preserve any edits
                }));
            }
        }
    }, [dataRes, user?.userId, isFetching]);

    const handleCreate = async () => {
        try {
            setIsSaving(true);

            const extraData: Record<string, any> = {};
            const vErrors: Record<string, string> = {};

            const isNonEmpty = (v: unknown) => {
                if (v === null || v === undefined) return false;
                if (typeof v === "string") return v.trim() !== "";
                return String(v).trim() !== "";
            };

            const isBtechFlow = admissionKind === "btech";

            if (isBtechFlow) {
                // ✅ Validate JEE
                const jErrMain = jeMainRef.current?.validate() ?? {};
                const jErrAdv = jeAdvRef.current?.validate() ?? {};

                // ✅ Get Olympiad data
                // const oyData = olympiadRef.current?.getData() ?? {};

                // ✅ Improved check (IMPORTANT)
                // const isValidValue = (v: any) => {
                //     if (v === null || v === undefined) return false;

                //     // File case
                //     if (v instanceof File) return true;

                //     // String case
                //     if (typeof v === "string") {
                //         return v.trim() !== "";
                //     }

                //     return true;
                // };

                // ✅ Only consider meaningful values
                // const isOlympiadFilled = Object.values(oyData).some(isValidValue);

                // ✅ Validate ONLY if filled
                let olympiadErrors = {};
                // if (isOlympiadFilled) {
                olympiadErrors = olympiadRef.current?.validate() ?? {};
                // }

                // ✅ Merge errors
                Object.assign(vErrors, jErrMain, jErrAdv, olympiadErrors);

                // ✅ Collect data
                const jm = jeMainRef.current?.getData() ?? {};
                const ja = jeAdvRef.current?.getData() ?? {};
                const oy = olympiadRef.current?.getData() ?? {};
                const mergeIfPresent = (target: any, source: any) => {
                    Object.entries(source || {}).forEach(([key, value]) => {
                        // if (value !== undefined && value !== null && value !== "") {
                        target[key] = value;
                        // }
                    });
                };
                console.log("jm", jm)
                console.log("ja", ja)
                console.log("oy", oy)
                mergeIfPresent(extraData, jm);
                mergeIfPresent(extraData, ja);
                mergeIfPresent(extraData, oy);

                // ✅ Type normalization
                if (extraData.jeemain_score) {
                    extraData.jeemain_score = parseFloat(String(extraData.jeemain_score));
                }
                if (extraData.jeeadv_score) {
                    extraData.jeeadv_score = parseFloat(String(extraData.jeeadv_score));
                }
            }

            setErrors(vErrors);

            if (Object.keys(vErrors).length > 0) {
                toast.error("Please complete all required fields.");
                return;
            }

            console.log("extraData", extraData);

            // ✅ SAVE using ViewModel
            const finalPayload = {
                ...dataToSave,   // 🔥 EXISTING FULL APPLICATION DATA
                ...extraData,    // 🔥 NEW EXAM DATA
            };

            console.log("FINAL PAYLOAD", dataToSave);

            await save(finalPayload);

            setShowToast(true);

            // ✅ refresh queries (optional but consistent)
            queryClient.invalidateQueries({
                queryKey: ["resourceData", "examDetails", user?.userId, localStorage.getItem("activeDriveId")],
            });

            // ✅ refresh sidebar progress
            await refreshProgress();

            toast.success("Exam details saved successfully!");
            if ((isDriveEditOpen && isAppSubmitted)) {
                setTimeout(() => navigate("/applicant/home"), 1000);
            }
            if (!(isDriveEditOpen && isAppSubmitted)) {
                setTimeout(() => navigate("/programmedetails"), 1000);
            }

        } catch (error) {
            console.error("Error saving exam details:", error);
            toast.error("Error saving details. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="dash-layout">
            {/* Sidebar */}
            <SidebarStudent
                sidebarCollapsed={sidebarCollapsed}
                toggleSidebar={() => setSidebarCollapsed((p) => !p)}
            />

            {/* Main Wrapper */}
            <div
                className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""
                    }`}
            >
                {/* Header */}
                <header className="dash-header">
                    <h1 className="dash-page-title">Exam Details</h1>

                    <div className="dash-user-profile">
                        <div
                            className="dash-profile-circle"
                            onClick={() => setShowDropdown((p) => !p)}
                        >
                            <span>S</span>
                        </div>

                        {showDropdown && (
                            <div className="dash-profile-dropdown">
                                <LogoutButton />
                            </div>
                        )}
                    </div>
                </header>

                {/* Content */}
                <div className="dash-content-body">
                    <div className="form-container">
                        <h3 className="form-subtitle">Exam Details</h3>

                        <div className="jee-forms-section" style={{ marginTop: "30px" }}>
                            <CreateJeemain
                                ref={jeMainRef}
                                variant="application"
                                initialApplicationRow={dataToSave}
                            />

                            <CreateJeeadvanced
                                ref={jeAdvRef}
                                variant="application"
                                initialApplicationRow={dataToSave}
                            />

                            <CreateOlympiadExport
                                ref={olympiadRef}
                                variant="application"
                                initialApplicationRow={dataToSave}
                            />
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="form-actions gap-2">
                        <button
                            className="save-btn"
                            type="button"
                            onClick={handleCreate}
                            disabled={isSaving}
                            style={{ background: "#28a745" }}
                        >
                            {isSaving ? "Saving..." : (isDriveEditOpen && isAppSubmitted) ? "Save" : "Save & Next"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}